import { MediaPickerComponent, WorkspaceAsset } from '@gripp/shared-logic';
import { ReactNode } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { tokens } from '../crop-circle/tokens';
import { AssetDetails } from './assetDetails';
import { AssetHeaderImage } from './assetHeaderImage';
import { AssetSyncState } from './types';

type AssetHeaderProps = {
  workspaceAsset: WorkspaceAsset;
  imgSize?: number;
  additionalHeaderBlock?: ReactNode;
  assetDetailsContainerStyle?: StyleProp<ViewStyle>;
  invalidateQuery?: () => void;
  mediaPicker?: MediaPickerComponent;
  onImagePress?: () => void;
  /** Set while the asset's creation is still on the device. */
  syncState?: AssetSyncState;
  /** Rendered above the routine pills; omitted callers show no description. */
  description?: ReactNode;
  /**
   * ALLOWLIST, not deny-list (plan 0030 Phase 5): a Guest's page is limited
   * to the hero image, name and description, so this suppresses AssetDetails
   * entirely -- it renders the last-known address (a maps link) and the
   * routine-state subheader, neither of which is on that allowlist. Defaults
   * to `false` so every other caller is unaffected.
   */
  isGuestOfAssetWorkspace?: boolean;
};

export const AssetHeader = ({
  isGuestOfAssetWorkspace = false,
  ...props
}: AssetHeaderProps) => {
  const { workspaceAsset } = props;

  return (
    workspaceAsset && (
      <View
        style={[
          styles.containerCropCircle,
          props.description !== undefined && styles.pinnedToTop,
        ]}
      >
        <AssetHeaderImage
          assetCoverImage={workspaceAsset?.asset?.coverImage}
          imgSize={props.imgSize}
          invalidateQuery={props.invalidateQuery}
          workspaceAsset={workspaceAsset}
          mediaPicker={props.mediaPicker}
          onImagePress={props.onImagePress}
        />
        {isGuestOfAssetWorkspace ? (
          props.description !== undefined && (
            <View style={styles.guestDetails}>{props.description}</View>
          )
        ) : (
          <AssetDetails
            workspaceAsset={workspaceAsset}
            syncState={props.syncState}
            description={props.description}
          />
        )}
      </View>
    )
  );
};

const styles = StyleSheet.create({
  containerCropCircle: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    columnGap: tokens.spacing.md,
    // Sized to the 88px cover image by default, but grows when the routine
    // pills wrap onto extra rows so the wrapped rows aren't clipped.
    minHeight: 88,
    marginTop: tokens.spacing.sm,
    marginBottom: tokens.spacing.xl,
    width: '100%',
  },
  // An expanding description grows the details column; centring would slide
  // the image down each time it expands, so pin it to the top instead.
  pinnedToTop: {
    alignItems: 'flex-start',
  },
  // Same column sizing as AssetDetails, so the description wraps beside the
  // image rather than pushing the row wider.
  guestDetails: {
    flex: 1,
    minWidth: 0,
  },
});
